import { MapWithDefault } from "@shared/common/types/MapWithDefault";
import { palette } from "@shared/EDS/ns-color-palette";
import { TrainUnitKind as TrainUnitKindCode } from "@opstel/query/models/planTrainUnit";

export const TRACK_LINE_WIDTH: number = 1;
export type EPDStyle = {
  textColor: string;
  trainTypeColors: MapWithDefault<TrainUnitKindCode, WithBackgroundColor & WithTextColor>;
  infraItems: {
    MapEdgePoint: WithTextColor & WithBorderColor; // Kaartrandpunt
    PlatformTrack: WithTextColor & WithBackgroundColor; // Perronspoor
    SetupTrack: WithTextColor & WithBackgroundColor; // Opstelspoor
    BorderPoint: WithLineColor; // Grenspunt
    BorderPointLabel: WithTextColor & WithBorderColor & WithLineColor; // GrenspuntLabel
    Switch: WithBackgroundColor; // Wissel
    SwitchLabel: WithTextColor & WithBorderColor; // WisselLabel
    RailSignal: WithLineColor; // Sein
    SignBoardProjection: WithLineColor; // BordProjectie
    Sign: WithLineColor; // Bord
    BufferStop: WithLineColor; // Stootjuk
    SectionOccupation: WithLineColor; // Sectiebezetting
  };
  tracks: EPDStyleTracks;
};

export type EPDStyleTracks = {
  default: WithLineStyles; // neutraal
  platform: WithLineStyles; // perron
  stablingArea: WithLineStyles; // opstelterrein
  networkMovement: WithLineStyles; // netwerkbeweging
  shuntingMovement: WithLineStyles; // rangeerbeweging
  outOfService: WithLineStyles; // buitendienst
  conflict: WithLineStyles; // conflict
  hover: WithLineStyle; // hover
};

type WithBackgroundColor = {
  backgroundColor: string;
};

type WithBorderColor = {
  borderColor: string;
};

type WithTextColor = {
  textColor: string;
};

type WithLineColor = {
  lineColor: string;
};

type LineType = "solid" | "dashed" | "inherit";

export type WithLineStyles = WithLineStyle[];

export type WithLineStyle = {
  lineStyle: LineType;
  lineWidth: number;
  lineColor: string;
};

const hoverActivatorTrackStyle: WithLineStyle = {
  lineStyle: "solid",
  lineWidth: 12,
  lineColor: palette.transparent,
};

const commonTrackColors: {
  networkMovement: WithLineStyles; // netwerkbeweging
  shuntingMovement: WithLineStyles; // rangeerbeweging
  outOfService: WithLineStyles; // buitendienst
  conflict: WithLineStyles; // conflict
  hover: WithLineStyle; // hover
} = {
  networkMovement: [
    hoverActivatorTrackStyle,
    {
      lineStyle: "solid",
      lineWidth: 2,
      lineColor: palette.blue400,
    },
  ],
  shuntingMovement: [
    hoverActivatorTrackStyle,
    {
      lineStyle: "solid",
      lineWidth: 2,
      lineColor: palette.green400,
    },
  ],
  outOfService: [
    {
      lineStyle: "solid",
      lineWidth: 12,
      lineColor: "#E6AA00",
    },
    {
      lineStyle: "solid",
      lineWidth: 2,
      lineColor: palette.gray600,
    },
  ],
  conflict: [
    hoverActivatorTrackStyle,
    {
      lineStyle: "solid",
      lineWidth: 2,
      lineColor: "#EC0045",
    },
  ],
  hover: {
    lineStyle: "inherit",
    lineWidth: 4,
    lineColor: "#FFA8FF",
  },
};

const commonTrainTypeColors: MapWithDefault<
  TrainUnitKindCode,
  WithBackgroundColor & WithTextColor
> = new MapWithDefault(
  new Map([
    ["SNG", { backgroundColor: palette.blue500, textColor: palette.white }],
    ["VIRM", { backgroundColor: palette.yellow500, textColor: "#003082" }],
    ["ICM", { backgroundColor: palette.green600, textColor: palette.white }],
    ["SLT", { backgroundColor: palette.purple500, textColor: palette.white }],
    ["FLIRT", { backgroundColor: palette.pink500, textColor: palette.white }],
    ["FLIRT FFF", { backgroundColor: palette.pink500, textColor: palette.white }],
    ["DDZ", { backgroundColor: palette.teal600, textColor: palette.white }],
    ["ICR", { backgroundColor: palette.orange500, textColor: palette.white }],
    ["ICNG", { backgroundColor: palette.yellow800, textColor: palette.white }],
    ["ICNG25", { backgroundColor: palette.yellow800, textColor: palette.white }],
    ["ICNGB25", { backgroundColor: palette.yellow800, textColor: palette.white }],
    ["SW7-15V", { backgroundColor: palette.teal400, textColor: palette.white }],
    ["SW7-25KV", { backgroundColor: palette.teal400, textColor: palette.white }],
    ["SW9-15V", { backgroundColor: palette.teal400, textColor: palette.white }],
    ["SW9-25KV", { backgroundColor: palette.teal400, textColor: palette.white }],
    ["SWB7-25KV", { backgroundColor: palette.teal400, textColor: palette.white }],
    ["FreightTrain", { backgroundColor: palette.gray400, textColor: palette.white }],
    ["ThirdParty", { backgroundColor: palette.gray400, textColor: palette.white }],
    ["Unknown NS", { backgroundColor: palette.gray700, textColor: palette.white }],
    ["Unknown 3rd party", { backgroundColor: palette.gray400, textColor: palette.white }],
  ]),
  "Unknown 3rd party",
);

export const light: EPDStyle = {
  textColor: palette.gray800,
  trainTypeColors: commonTrainTypeColors,
  infraItems: {
    MapEdgePoint: {
      borderColor: "#0063D3",
      textColor: "#0063D3",
    },
    PlatformTrack: {
      backgroundColor: palette.gray600,
      textColor: palette.white,
    },
    SetupTrack: {
      backgroundColor: palette.gray600,
      textColor: palette.white,
    },
    BorderPoint: {
      lineColor: palette.gray600,
    },
    BorderPointLabel: {
      borderColor: palette.gray600,
      textColor: palette.gray600,
      lineColor: palette.gray600,
    },
    Switch: {
      backgroundColor: palette.blue500,
    },
    SwitchLabel: {
      borderColor: palette.gray600,
      textColor: palette.gray600,
    },
    RailSignal: {
      lineColor: palette.gray600,
    },
    SignBoardProjection: {
      lineColor: palette.gray600,
    },
    Sign: {
      lineColor: palette.gray600,
    },
    BufferStop: {
      lineColor: palette.gray600,
    },
    SectionOccupation: {
      lineColor: palette.gray900,
    },
  },
  tracks: {
    ...commonTrackColors,
    default: [
      hoverActivatorTrackStyle,
      {
        lineStyle: "solid",
        lineWidth: 2,
        lineColor: palette.gray600,
      },
    ],
    platform: [
      {
        lineStyle: "solid",
        lineWidth: 20,
        lineColor: palette.gray300,
      },
      {
        lineStyle: "solid",
        lineWidth: 2,
        lineColor: palette.gray600,
      },
    ],
    stablingArea: [
      hoverActivatorTrackStyle,
      {
        lineStyle: "dashed",
        lineWidth: 2,
        lineColor: palette.gray600,
      },
    ],
  },
};

export const dark: EPDStyle = {
  textColor: palette.gray300,
  trainTypeColors: commonTrainTypeColors,
  infraItems: {
    MapEdgePoint: {
      borderColor: "#96C7FA",
      textColor: "#96C7FA",
    },
    PlatformTrack: {
      backgroundColor: palette.gray950,
      textColor: palette.white,
    },
    SetupTrack: {
      backgroundColor: palette.gray500,
      textColor: palette.white,
    },
    BorderPoint: {
      lineColor: palette.gray200,
    },
    BorderPointLabel: {
      borderColor: palette.gray200,
      textColor: palette.gray200,
      lineColor: palette.gray200,
    },
    Switch: {
      backgroundColor: palette.blue500,
    },
    SwitchLabel: {
      borderColor: palette.gray200,
      textColor: palette.gray200,
    },
    RailSignal: {
      lineColor: palette.gray200,
    },
    SignBoardProjection: {
      lineColor: palette.gray200,
    },
    Sign: {
      lineColor: palette.gray200,
    },
    BufferStop: {
      lineColor: palette.gray200,
    },
    SectionOccupation: {
      lineColor: palette.gray100,
    },
  },
  tracks: {
    ...commonTrackColors,
    default: [
      hoverActivatorTrackStyle,
      {
        lineStyle: "solid",
        lineWidth: 2,
        lineColor: palette.gray200,
      },
    ],
    platform: [
      {
        lineStyle: "solid",
        lineWidth: 20,
        lineColor: palette.gray500,
      },
      {
        lineStyle: "solid",
        lineWidth: 2,
        lineColor: palette.gray600,
      },
    ],
    stablingArea: [
      hoverActivatorTrackStyle,
      {
        lineStyle: "dashed",
        lineWidth: 2,
        lineColor: palette.gray200,
      },
    ],
  },
};
